import type { ParkingSpotOccupancyMap } from "./parkingList";

export type ParkingSpotTypeValue = "space_regular" | "space_handi" | "space_family" | "space_disabled" | "space_ev";
export type ParkingSpotTypeMap = Record<string, ParkingSpotTypeValue>;

type ParkingSpotTypesAreaCacheEntry = {
  updatedAt: number;
  spots: ParkingSpotTypeMap;
};

type ParkingSpotTypesParkingCacheEntry = {
  updatedAt: number;
  areas: Record<string, ParkingSpotTypesAreaCacheEntry>;
};

type ParkingSpotTypesCache = Record<string, ParkingSpotTypesParkingCacheEntry>;

type SpecialFreeSpotCounts = {
  freeHandi: number | null;
  freeEv: number | null;
};

const STORAGE_KEY = "parking-box:parking-spot-types:v1";
const KNOWN_SPOT_TYPES = new Set<ParkingSpotTypeValue>([
  "space_regular",
  "space_handi",
  "space_family",
  "space_disabled",
  "space_ev",
]);

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const tryParseJson = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const resolveMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.length) {
    return null;
  }
  return tryParseJson(trimmed) ?? value;
};

const normalizeSpotType = (value: unknown): ParkingSpotTypeValue => {
  if (typeof value === "string") {
    const trimmed = value.trim() as ParkingSpotTypeValue;
    if (KNOWN_SPOT_TYPES.has(trimmed)) {
      return trimmed;
    }
  }
  return "space_regular";
};

const normalizeCacheId = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const extractParkingSpotsArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (depth > 5 || payload === null || typeof payload === "undefined") {
    return null;
  }

  const resolved = resolveMaybeJson(payload);
  if (Array.isArray(resolved)) {
    const looksLikeDirectSpotArray = resolved.some(
      (entry) => Boolean(entry) && typeof entry === "object" && ("id" in (entry as Record<string, unknown>) || "type" in (entry as Record<string, unknown>)),
    );
    if (looksLikeDirectSpotArray) {
      return resolved;
    }

    for (const entry of resolved) {
      const nested = extractParkingSpotsArray(entry, depth + 1);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (!resolved || typeof resolved !== "object") {
    return null;
  }

  const record = resolved as Record<string, unknown>;
  const candidates = [
    record.spots,
    record.Spots,
    record.layout_spots,
    record.slots,
    record._data_,
    record.data,
    record.payload,
    record.body,
    record.response,
    record.results,
    record.result,
  ];

  for (const candidate of candidates) {
    if (!candidate || candidate === resolved) {
      continue;
    }
    const nested = extractParkingSpotsArray(candidate, depth + 1);
    if (nested) {
      return nested;
    }
  }

  for (const value of Object.values(record)) {
    if (!value || value === resolved) {
      continue;
    }
    const nested = extractParkingSpotsArray(value, depth + 1);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const sanitizeSpotTypeMap = (value: unknown): ParkingSpotTypeMap => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<ParkingSpotTypeMap>((accumulator, [spotId, spotType]) => {
    const normalizedSpotId = normalizeCacheId(spotId);
    if (!normalizedSpotId) {
      return accumulator;
    }
    accumulator[normalizedSpotId] = normalizeSpotType(spotType);
    return accumulator;
  }, {});
};

const sanitizeAreaCacheEntry = (value: unknown): ParkingSpotTypesAreaCacheEntry | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const spots = sanitizeSpotTypeMap(record.spots);
  if (!Object.keys(spots).length) {
    return null;
  }

  const updatedAtCandidate = typeof record.updatedAt === "number" && Number.isFinite(record.updatedAt) ? record.updatedAt : Date.now();
  return {
    updatedAt: updatedAtCandidate,
    spots,
  };
};

const sanitizeParkingCacheEntry = (value: unknown): ParkingSpotTypesParkingCacheEntry | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const areasRaw = record.areas;
  if (!areasRaw || typeof areasRaw !== "object" || Array.isArray(areasRaw)) {
    return null;
  }

  const areas = Object.entries(areasRaw as Record<string, unknown>).reduce<Record<string, ParkingSpotTypesAreaCacheEntry>>((accumulator, [areaId, areaValue]) => {
    const normalizedAreaId = normalizeCacheId(areaId);
    const normalizedEntry = sanitizeAreaCacheEntry(areaValue);
    if (!normalizedAreaId || !normalizedEntry) {
      return accumulator;
    }
    accumulator[normalizedAreaId] = normalizedEntry;
    return accumulator;
  }, {});

  if (!Object.keys(areas).length) {
    return null;
  }

  const updatedAtCandidate = typeof record.updatedAt === "number" && Number.isFinite(record.updatedAt) ? record.updatedAt : Date.now();
  return {
    updatedAt: updatedAtCandidate,
    areas,
  };
};

const readCache = (): ParkingSpotTypesCache => {
  if (!isBrowser()) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed as Record<string, unknown>).reduce<ParkingSpotTypesCache>((accumulator, [parkingId, entry]) => {
      const normalizedParkingId = normalizeCacheId(parkingId);
      const normalizedEntry = sanitizeParkingCacheEntry(entry);
      if (!normalizedParkingId || !normalizedEntry) {
        return accumulator;
      }
      accumulator[normalizedParkingId] = normalizedEntry;
      return accumulator;
    }, {});
  } catch {
    return {};
  }
};

const writeCache = (cache: ParkingSpotTypesCache) => {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // ignore localStorage quota / access errors
  }
};

const mergeAreaSpotTypes = (entry: ParkingSpotTypesParkingCacheEntry | null | undefined): ParkingSpotTypeMap => {
  if (!entry) {
    return {};
  }

  return Object.values(entry.areas).reduce<ParkingSpotTypeMap>((accumulator, areaEntry) => {
    Object.assign(accumulator, areaEntry.spots);
    return accumulator;
  }, {});
};

export const parseParkingSpotTypesResponse = (payload: unknown): ParkingSpotTypeMap => {
  const source = extractParkingSpotsArray(payload);
  if (!source) {
    return {};
  }

  return source.reduce<ParkingSpotTypeMap>((accumulator, entry) => {
    if (!entry || typeof entry !== "object") {
      return accumulator;
    }

    const record = entry as Record<string, unknown>;
    const spotId = normalizeCacheId(record.id ?? record.spot_id ?? record.spotId);
    if (!spotId) {
      return accumulator;
    }

    accumulator[spotId] = normalizeSpotType(record.type ?? record.spot_type ?? record.spotType);
    return accumulator;
  }, {});
};

export const readCachedParkingSpotTypes = (parkingId: string): ParkingSpotTypeMap => {
  const normalizedParkingId = normalizeCacheId(parkingId);
  if (!normalizedParkingId) {
    return {};
  }

  const cache = readCache();
  return mergeAreaSpotTypes(cache[normalizedParkingId]);
};

export const readCachedParkingSpotTypesMany = (parkingIds: string[]): Record<string, ParkingSpotTypeMap> =>
  parkingIds.reduce<Record<string, ParkingSpotTypeMap>>((accumulator, parkingId) => {
    const normalizedParkingId = normalizeCacheId(parkingId);
    if (!normalizedParkingId) {
      return accumulator;
    }
    const spotTypes = readCachedParkingSpotTypes(normalizedParkingId);
    if (!Object.keys(spotTypes).length) {
      return accumulator;
    }
    accumulator[normalizedParkingId] = spotTypes;
    return accumulator;
  }, {});

export const writeCachedParkingSpotTypesArea = (
  parkingId: string,
  areaId: string,
  spotTypes: ParkingSpotTypeMap,
): ParkingSpotTypeMap => {
  const normalizedParkingId = normalizeCacheId(parkingId);
  const normalizedAreaId = normalizeCacheId(areaId);
  if (!normalizedParkingId || !normalizedAreaId || !Object.keys(spotTypes).length) {
    return {};
  }

  const cache = readCache();
  const previousEntry = cache[normalizedParkingId];
  const updatedAt = Date.now();
  const nextEntry: ParkingSpotTypesParkingCacheEntry = {
    updatedAt,
    areas: {
      ...(previousEntry?.areas ?? {}),
      [normalizedAreaId]: {
        updatedAt,
        spots: { ...spotTypes },
      },
    },
  };

  cache[normalizedParkingId] = nextEntry;
  writeCache(cache);
  return mergeAreaSpotTypes(nextEntry);
};

export const countFreeSpecialParkingSpots = (
  spotOccupancy: ParkingSpotOccupancyMap | null | undefined,
  spotTypes: ParkingSpotTypeMap | null | undefined,
): SpecialFreeSpotCounts => {
  if (!spotOccupancy || !Object.keys(spotOccupancy).length || !spotTypes || !Object.keys(spotTypes).length) {
    return { freeHandi: null, freeEv: null };
  }

  let freeHandi = 0;
  let freeEv = 0;

  Object.entries(spotTypes).forEach(([spotId, spotType]) => {
    const occupancy = spotOccupancy[spotId];
    if (occupancy !== 0) {
      return;
    }

    if (spotType === "space_handi") {
      freeHandi += 1;
      return;
    }

    if (spotType === "space_ev") {
      freeEv += 1;
    }
  });

  return { freeHandi, freeEv };
};
